/**
 * Where each holding is domiciled, when the issuer does not say.
 *
 * NOT PUBLISHED DATA, on the same terms as sectors.ts — this is the app's own
 * classification and lives here rather than in `shared/data` for that reason.
 *
 * Two shapes, because most of these funds are single-country by construction and
 * spelling out every ticker would be a hundred and fifty lines that say the same
 * word. A fund whose every holding shares a domicile declares it once in
 * FUND_COUNTRY; anything that disagrees gets a per-fund override.
 *
 * Keyed by fund rather than globally, for the same reason sectors are: tickers
 * collide across markets. `T` is TELUS inside a Canadian utilities fund and AT&T
 * inside a US one, `H` is Hydro One here and Hyatt there, `EDV` is Endeavour
 * Mining in a gold fund and a Vanguard treasury ETF in a bond one. A global
 * ticker→country map would have to pick one and be wrong on the other page —
 * `bun run check` already lists six such collisions in the holdings data.
 *
 * The labels match the names the issuers use in their published geographic
 * breakdowns ("United States", "Canada"), because a click is matched against
 * them and the two sources have to agree.
 */

/** Every holding of this fund is domiciled here, unless overridden below. */
export const FUND_COUNTRY: Record<string, string> = {
    // Canadian banks, lifecos, utilities, pipelines and rails.
    HMAX: 'Canada',
    UMAX: 'Canada',
    HFN: 'Canada',
    HFIN: 'Canada',
    CMVP: 'Canada',
    UMVP: 'Canada',

    // US large-cap equity, technology, healthcare and financials.
    QMAX: 'United States',
    SMAX: 'United States',
    LMAX: 'United States',
    FMAX: 'United States',
    QMVP: 'United States',
    'SMVP-U': 'United States',
};

/**
 * Exceptions, and holdings of funds that are not single-country.
 *
 * The DayMAX funds hold one Hamilton fund plus one third-party index ETF. The
 * Hamilton side resolves by looking through it; the third-party side has no
 * holdings in the artefact — we do not scrape Vanguard or Invesco — so it is
 * named here instead. Both are plain US index funds and there is nothing
 * arguable about it.
 */
export const HOLDING_COUNTRIES: Record<string, Record<string, string>> = {
    CDAY: { VOO: 'United States' },
    SDAY: { VOO: 'United States' },
    QDAY: { QQQM: 'United States' },
};

/**
 * The app's country for one holding, or null where it has not been classified.
 *
 * Deliberately absent: Linde in SMAX, which is Irish-domiciled and sits in a US
 * equity fund, and Mobility Global in SMVP-U. Both are single holdings inside
 * funds otherwise wholly American, and either answer is defensible — so they
 * take the fund's country rather than being singled out on a judgement the
 * issuer has not made. Called out here so that stays a decision rather than an
 * oversight.
 */
export const classifiedCountry = (fund: string, ticker: string | null): string | null =>
    ticker == null ? null : (HOLDING_COUNTRIES[fund]?.[ticker] ?? FUND_COUNTRY[fund] ?? null);
